import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { 
  Tag, Plus, Edit, Trash2, Copy, CheckCircle, XCircle, DollarSign,
  Percent, Gift, Calendar, Users, RefreshCw 
} from "lucide-react";
import { AgentLayout } from "./AgentDashboard";
import { API } from "../../App";
import axios from "axios";
import { toast } from "sonner";

export default function AgentPromoCodes() {
  const [loading, setLoading] = useState(true);
  const [promoCodes, setPromoCodes] = useState([]);
  const [stats, setStats] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedCode, setSelectedCode] = useState(null);
  
  const [form, setForm] = useState({
    code: "",
    description: "",
    discount_type: "percentage",
    discount_value: 10,
    applies_to: "first_payment",
    duration_months: 1,
    max_uses: null,
    max_uses_per_user: 1,
    min_purchase_amount: null,
    expires_at: "",
    is_active: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      const [codesRes, statsRes] = await Promise.all([
        axios.get(`${API}/agent/promo-codes`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/agent/promo-codes/stats/summary`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setPromoCodes(codesRes.data.promo_codes || []);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Failed to load promo codes");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      code: "",
      description: "",
      discount_type: "percentage",
      discount_value: 10,
      applies_to: "first_payment",
      duration_months: 1,
      max_uses: null,
      max_uses_per_user: 1,
      min_purchase_amount: null,
      expires_at: "",
      is_active: true
    });
    setEditMode(false);
    setSelectedCode(null);
  };

  const handleCreate = async () => {
    if (!form.code) {
      toast.error("Promo code is required");
      return;
    }
    
    const token = localStorage.getItem("agent_token");
    try {
      const payload = {
        ...form,
        max_uses: form.max_uses || null,
        min_purchase_amount: form.min_purchase_amount || null,
        expires_at: form.expires_at || null
      };
      
      if (editMode && selectedCode) {
        await axios.put(`${API}/agent/promo-codes/${selectedCode.promo_code_id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Promo code updated");
      } else {
        await axios.post(`${API}/agent/promo-codes`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Promo code created");
      }
      
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save promo code");
    }
  };

  const handleEdit = (code) => {
    setSelectedCode(code);
    setForm({
      code: code.code,
      description: code.description || "",
      discount_type: code.discount_type,
      discount_value: code.discount_value,
      applies_to: code.applies_to,
      duration_months: code.duration_months || 1,
      max_uses: code.max_uses,
      max_uses_per_user: code.max_uses_per_user || 1,
      min_purchase_amount: code.min_purchase_amount,
      expires_at: code.expires_at ? code.expires_at.split('T')[0] : "",
      is_active: code.is_active
    });
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleDelete = async (promoCodeId) => {
    if (!confirm("Are you sure you want to delete this promo code?")) return;
    
    const token = localStorage.getItem("agent_token");
    try {
      await axios.delete(`${API}/agent/promo-codes/${promoCodeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Promo code deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete promo code");
    }
  };

  const handleToggleActive = async (code) => {
    const token = localStorage.getItem("agent_token");
    try {
      await axios.put(`${API}/agent/promo-codes/${code.promo_code_id}`, 
        { is_active: !code.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Promo code ${!code.is_active ? 'activated' : 'deactivated'}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update promo code");
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Code copied to clipboard");
  };

  const getDiscountDisplay = (code) => {
    if (code.discount_type === "percentage") {
      return `${code.discount_value}% off`;
    } else if (code.discount_type === "fixed") {
      return `$${code.discount_value} off`;
    } else if (code.discount_type === "free_month") {
      return "1 Month Free";
    }
    return code.discount_value;
  };

  const getAppliesTo = (applies_to) => {
    const labels = {
      first_payment: "First Payment",
      all_payments: "All Payments",
      duration: "Limited Duration"
    };
    return labels[applies_to] || applies_to;
  };

  return (
    <AgentLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Young Serif, serif' }}>
              <Tag className="inline h-8 w-8 mr-3 text-primary" />
              Promo Codes
            </h1>
            <p className="text-muted-foreground mt-1">
              Create and manage discount codes for your customers
            </p>
          </div>
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} data-testid="create-promo-btn">
            <Plus className="h-4 w-4 mr-2" />
            Create Promo Code
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="card-organic">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Codes</p>
                    <p className="text-2xl font-bold">{stats.total_codes}</p>
                  </div>
                  <Tag className="h-8 w-8 text-primary opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card className="card-organic">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Active Codes</p>
                    <p className="text-2xl font-bold text-green-600">{stats.active_codes}</p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-green-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card className="card-organic">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Uses</p>
                    <p className="text-2xl font-bold text-blue-600">{stats.total_uses}</p>
                  </div>
                  <Users className="h-8 w-8 text-blue-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card className="card-organic">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Discounts Given</p>
                    <p className="text-2xl font-bold text-purple-600">
                      ${stats.total_discount_given?.toFixed(2) || '0.00'}
                    </p>
                  </div>
                  <DollarSign className="h-8 w-8 text-purple-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Promo Codes Table */}
        <Card className="card-organic">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>All Promo Codes</CardTitle>
              <CardDescription>Manage discount codes and track usage</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={fetchData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : promoCodes.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No promo codes yet. Create your first promo code!
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Discount</th>
                      <th>Applies To</th>
                      <th>Uses</th>
                      <th>Status</th>
                      <th>Expires</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {promoCodes.map(code => (
                      <tr key={code.promo_code_id}>
                        <td>
                          <div className="flex items-center gap-2">
                            <code className="bg-muted px-2 py-1 rounded font-mono text-sm">
                              {code.code}
                            </code>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => copyCode(code.code)}
                              className="h-6 w-6 p-0"
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                          {code.description && (
                            <p className="text-xs text-muted-foreground mt-1">{code.description}</p>
                          )}
                        </td>
                        <td>
                          <Badge className="bg-green-100 text-green-800">
                            {getDiscountDisplay(code)}
                          </Badge>
                        </td>
                        <td>{getAppliesTo(code.applies_to)}</td>
                        <td>
                          {code.current_uses || 0}
                          {code.max_uses && <span className="text-muted-foreground">/{code.max_uses}</span>}
                        </td>
                        <td>
                          <Switch
                            checked={code.is_active}
                            onCheckedChange={() => handleToggleActive(code)}
                          />
                        </td>
                        <td>
                          {code.expires_at 
                            ? new Date(code.expires_at).toLocaleDateString()
                            : "Never"
                          }
                        </td>
                        <td className="space-x-1">
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => handleEdit(code)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => handleDelete(code.promo_code_id)}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editMode ? "Edit Promo Code" : "Create Promo Code"}</DialogTitle>
            <DialogDescription>
              {editMode ? "Update the promo code details" : "Set up a new discount code for your customers"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Promo Code *</Label>
                <Input
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  placeholder="SAVE20"
                  disabled={editMode}
                  data-testid="promo-code-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Active</Label>
                <div className="pt-2">
                  <Switch
                    checked={form.is_active}
                    onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
                  />
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="20% off first payment"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Discount Type</Label>
                <Select 
                  value={form.discount_type} 
                  onValueChange={(value) => setForm({ ...form, discount_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage Off</SelectItem>
                    <SelectItem value="fixed">Fixed Amount Off</SelectItem>
                    <SelectItem value="free_month">Free Month</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>
                  {form.discount_type === "percentage" ? "Percentage (%)" : "Amount ($)"}
                </Label>
                <Input
                  type="number"
                  value={form.discount_value}
                  onChange={(e) => setForm({ ...form, discount_value: parseFloat(e.target.value) || 0 })}
                  disabled={form.discount_type === "free_month"}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Applies To</Label>
                <Select 
                  value={form.applies_to} 
                  onValueChange={(value) => setForm({ ...form, applies_to: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="first_payment">First Payment Only</SelectItem>
                    <SelectItem value="all_payments">All Payments</SelectItem>
                    <SelectItem value="duration">Limited Duration</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.applies_to === "duration" && (
                <div className="space-y-2">
                  <Label>Duration (Months)</Label>
                  <Input
                    type="number"
                    value={form.duration_months}
                    onChange={(e) => setForm({ ...form, duration_months: parseInt(e.target.value) || 1 })}
                    min={1}
                  />
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Max Total Uses</Label>
                <Input
                  type="number"
                  value={form.max_uses || ""}
                  onChange={(e) => setForm({ ...form, max_uses: e.target.value ? parseInt(e.target.value) : null })}
                  placeholder="Unlimited"
                />
              </div>
              <div className="space-y-2">
                <Label>Max Uses Per User</Label>
                <Input
                  type="number"
                  value={form.max_uses_per_user}
                  onChange={(e) => setForm({ ...form, max_uses_per_user: parseInt(e.target.value) || 1 })}
                  min={1}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Min Purchase ($)</Label>
                <Input
                  type="number"
                  value={form.min_purchase_amount || ""}
                  onChange={(e) => setForm({ ...form, min_purchase_amount: e.target.value ? parseFloat(e.target.value) : null })}
                  placeholder="No minimum"
                />
              </div>
              <div className="space-y-2">
                <Label>Expires On</Label>
                <Input
                  type="date"
                  value={form.expires_at}
                  onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleCreate} data-testid="save-promo-btn">
              {editMode ? "Update" : "Create"} Promo Code
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AgentLayout>
  );
}
